// RequestHistory — inline list of the last 10 calls, filtered to the current operation.
// Clicking a row re-loads the snapshot back into the form via onReplay.

import { useEffect } from 'react';
import { Trash2, RotateCcw, History } from 'lucide-react';
import MethodBadge from '@/components/layout/MethodBadge';
import { useHistoryStore, type HistoryItem } from '@/stores/history.store';
import type { Method } from '@/lib/tokens';

interface Props {
    operationId?: string;
    onReplay?: (snapshot: {
        pathParams?: Record<string, string>;
        queryParams?: Record<string, string>;
        body?: string;
    }) => void;
}

export default function RequestHistory({ operationId, onReplay }: Props) {
    const history = useHistoryStore((s) => s.history);
    const loadFromStorage = useHistoryStore((s) => s.loadFromStorage);
    const clearAll = useHistoryStore((s) => s.clearAll);

    useEffect(() => { loadFromStorage(); }, [loadFromStorage]);

    // Show only entries for this operation when an operationId is supplied,
    // otherwise show everything.
    const filtered = operationId
        ? history.filter((h) => h.operationId === operationId)
        : history;

    if (filtered.length === 0) return null;

    return (
        <div className="pt-4" style={{ borderTop: '1px dashed var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
                <h3 className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink uppercase tracking-wider">
                    <History className="h-3 w-3 text-ghost" /> Riwayat
                </h3>
                <button
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 text-xxs text-muted hover:text-ink transition-colors px-2 py-1 rounded"
                    aria-label="Hapus riwayat"
                >
                    <Trash2 className="h-3 w-3" /> Hapus
                </button>
            </div>
            <ul className="space-y-1">
                {filtered.map((h) => (
                    <HistoryRow key={h.id} item={h} onReplay={onReplay} />
                ))}
            </ul>
        </div>
    );
}

function HistoryRow({ item, onReplay }: { item: HistoryItem; onReplay?: Props['onReplay'] }) {
    const m = (item.method.toUpperCase() as Method);
    const color = statusColor(item.status);
    return (
        <li>
            <button
                onClick={() => onReplay?.({
                    pathParams: item.pathParams,
                    queryParams: item.queryParams,
                    body: item.body,
                })}
                className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded transition-colors hover:bg-[var(--surface-hover)]"
                style={{ backgroundColor: 'transparent' }}
                aria-label={`Replay ${item.method} ${item.path}`}
            >
                <MethodBadge method={m} size="sm" />
                <code className="font-mono text-xxs text-muted flex-1 truncate">{item.path}</code>
                <span
                    className="font-mono text-xxs font-semibold px-1.5 py-0.5 rounded"
                    style={{ color, backgroundColor: `${color}22` }}
                >
                    {item.status || 'ERR'}
                </span>
                <span className="font-mono text-xxs text-ghost w-12 text-right">{item.elapsedMs}ms</span>
                <span className="text-xxs text-ghost w-16 text-right">{timeAgo(item.ts)}</span>
                <RotateCcw className="h-3 w-3 text-ghost shrink-0" />
            </button>
        </li>
    );
}

function statusColor(status: number): string {
    if (status >= 200 && status < 300) return 'var(--m-get)';
    if (status >= 400 && status < 500) return 'var(--warn)';
    if (status >= 500 || status === 0) return 'var(--err)';
    return 'var(--muted)';
}

function timeAgo(ts: number): string {
    const diff = Math.max(0, Date.now() - ts);
    const sec = Math.round(diff / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}j`;
    const day = Math.round(hr / 24);
    return `${day}h`;
}
